import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const RECONNECT_DELAY_MS = 3000;

// Every distinct SSE event name consumed anywhere in the app today. EventSource
// has no "any event" hook -- each named event needs its own addEventListener --
// so this allow-list is what the single shared connection wires up on every
// (re)connect. Add an entry here whenever the backend starts broadcasting a
// new event type that a component needs to subscribe to.
const EVENT_NAMES = [
  'new_incident',
  'incident_updated',
  'incident_dispatched',
  'incident_message',
  'case_typing',
  'case_read',
  'responder_updated',
  'responder_deleted',
  'responder_location_updated',
  'notification',
  'telephony_call',
  'intake_session',
  'user_updated',
  'user_deleted',
];

const SSEContext = createContext(null);

// Single shared SSE connection for the whole app. Before this, six hooks in
// useIncidents.js plus useRequesterIncidents.js, TopNav's IncomingCallWatcher,
// and IncidentChatThread each opened their own `new EventSource(...)` -- a
// command-center page could hold 6+ simultaneous streams to /api/events,
// brushing the browser's 6-per-host HTTP/1.1 connection cap and starving
// ordinary fetch() calls. This provider opens exactly one connection per
// session (per role -- requesters hit /api/requester/events, everyone else
// hits /api/events) and fans events out to subscribers via subscribe().
export function SSEProvider({ children }) {
  const { token, isRequester } = useAuth();
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Map()); // eventName -> Set<(data) => void>
  const esRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const dispatch = useCallback((eventName, event) => {
    const handlers = listenersRef.current.get(eventName);
    if (!handlers || handlers.size === 0) return;
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }
    handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (err) {
        console.error(`SSE handler for "${eventName}" threw`, err);
      }
    });
  }, []);

  useEffect(() => {
    if (!token) {
      setConnected(false);
      return undefined;
    }

    const endpoint = isRequester ? '/api/requester/events' : '/api/events';
    let cancelled = false;

    function connect() {
      if (cancelled) return;
      const es = new EventSource(`${API_URL}${endpoint}?token=${encodeURIComponent(token)}`);
      esRef.current = es;

      es.onopen = () => setConnected(true);

      EVENT_NAMES.forEach((name) => {
        es.addEventListener(name, (event) => dispatch(name, event));
      });

      es.onerror = () => {
        setConnected(false);
        es.close();
        if (!cancelled && reconnectTimerRef.current === null) {
          reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
          }, RECONNECT_DELAY_MS);
        }
      };
    }

    connect();

    return () => {
      cancelled = true;
      setConnected(false);
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      esRef.current?.close();
      esRef.current = null;
    };
    // isRequester selects the endpoint; token changes (login/logout) tear
    // down and re-establish the connection.
  }, [token, isRequester, dispatch]);

  const subscribe = useCallback((eventName, handler) => {
    if (!listenersRef.current.has(eventName)) {
      listenersRef.current.set(eventName, new Set());
    }
    listenersRef.current.get(eventName).add(handler);
    return () => {
      listenersRef.current.get(eventName)?.delete(handler);
    };
  }, []);

  const value = useMemo(() => ({ subscribe, connected }), [subscribe, connected]);

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
}

export function useSSEContext() {
  const ctx = useContext(SSEContext);
  if (!ctx) throw new Error('useSSEContext must be used within an SSEProvider');
  return ctx;
}

// Subscribe `handler` to a named SSE event for the lifetime of the calling
// component/hook. `handler` is kept in a ref so callers can pass an inline
// arrow function on every render without needing to useCallback it themselves
// -- only `eventName` changing re-subscribes.
export function useSSESubscription(eventName, handler) {
  const { subscribe } = useSSEContext();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => subscribe(eventName, (data) => handlerRef.current(data)), [eventName, subscribe]);
}

// True once the shared connection is open. Consumers use this to decide
// whether to run their own fallback polling -- each hook keeps its existing
// per-resource poll function/interval, this just tells it when SSE isn't
// delivering so the UI doesn't go stale.
export function useSSEConnected() {
  return useSSEContext().connected;
}
